import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Activity, AlertCircle, AlertTriangle, CheckCircle2, ExternalLink, Loader2, Play } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/seo-ops/health`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
const FILTERS = [["problems", "Errors"], ["warnings", "Warnings"], ["all", "All pages"]];

const Stat = ({ label, value, cls, testid }) => (
  <div className="rounded-lg border border-slate-200 bg-white px-4 py-3" data-testid={testid}>
    <p className="text-xs text-slate-500">{label}</p><p className={`text-2xl font-bold ${cls}`}>{value}</p>
  </div>
);

const PageRow = ({ p, base }) => {
  const errs = p.issues.filter((i) => i.level === "error");
  return (
    <div className="border-b border-slate-100 py-3" data-testid={`seo-health-row-${p.path}`}>
      <div className="flex items-center gap-2 flex-wrap">
        {errs.length ? <AlertCircle className="w-4 h-4 text-red-500" /> : p.issues.length ? <AlertTriangle className="w-4 h-4 text-amber-500" /> : <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
        <a href={`${base}${p.path}`} target="_blank" rel="noreferrer" className="font-medium text-navy hover:text-teal text-sm inline-flex items-center gap-1">{p.path}<ExternalLink className="w-3 h-3" /></a>
        <span className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{p.type}</span>
      </div>
      <p className="text-xs text-slate-500 mt-1 ml-6 truncate">Google sees: <span className="text-slate-700">{p.title || "(no title)"}</span></p>
      <ul className="ml-6 mt-1 space-y-0.5">
        {p.issues.map((i, n) => <li key={n} className={`text-xs ${i.level === "error" ? "text-red-600" : "text-amber-700"}`}>{i.msg}</li>)}
      </ul>
    </div>
  );
};

export const SeoHealthCheck = () => {
  const [d, setD] = useState(null);
  const [filter, setFilter] = useState("problems");
  const load = useCallback(() => axios.get(API, auth()).then((r) => setD(r.data)).catch(() => toast.error("Couldn't load SEO health check")), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (d?.status !== "running") return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [d?.status, load]);

  const run = async () => {
    try { await axios.post(`${API}/run`, {}, auth()); toast.info("Checking live pages… this takes about a minute"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't start the check"); }
  };
  const pages = useMemo(() => (d?.pages || []).filter((p) =>
    filter === "all" ? true : filter === "problems" ? p.issues.some((i) => i.level === "error") : p.issues.length > 0 && !p.issues.some((i) => i.level === "error")), [d, filter]);
  const running = d?.status === "running";

  return (
    <Card className="border-slate-200" data-testid="seo-health-check">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="font-heading font-semibold text-navy text-lg flex items-center gap-2"><Activity className="w-5 h-5 text-teal" />Live SEO Health Check</h3>
            <p className="text-sm text-slate-500">Loads the live site the way Google does and flags missing, generic or wrong titles and descriptions, plus duplicates, length and canonical links.</p>
            {d?.finished_at && !running && <p className="text-xs text-slate-400 mt-1" data-testid="seo-health-last-run">Last run {new Date(d.finished_at).toLocaleString()}{d.started_by ? ` by ${d.started_by}` : ""} · {d.base}</p>}
          </div>
          <Button onClick={run} disabled={running} className="bg-teal hover:bg-teal-dark text-white" data-testid="seo-health-run-btn">
            {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}{running ? "Checking…" : "Run check"}
          </Button>
        </div>
        {d?.status === "failed" && <p className="text-sm text-red-600" data-testid="seo-health-error">The check failed: {d.error}</p>}
        {d?.status === "never" && <p className="text-sm text-slate-500" data-testid="seo-health-never">No check has been run yet.</p>}
        {d?.summary && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat label="Pages checked" value={d.summary.pages} cls="text-navy" testid="seo-health-pages" />
              <Stat label="Pages with errors" value={d.summary.pages_with_errors} cls="text-red-600" testid="seo-health-errors" />
              <Stat label="Warnings" value={d.summary.warnings} cls="text-amber-600" testid="seo-health-warnings" />
              <Stat label="No issues" value={d.summary.clean} cls="text-emerald-600" testid="seo-health-clean" />
            </div>
            <div className="flex gap-2">
              {FILTERS.map(([k, l]) => (
                <button key={k} onClick={() => setFilter(k)} data-testid={`seo-health-filter-${k}`}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filter === k ? "bg-navy text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{l}</button>
              ))}
            </div>
            <div className="max-h-[560px] overflow-y-auto pr-1">
              {pages.length === 0 ? <p className="text-sm text-slate-500 py-6 text-center" data-testid="seo-health-empty">Nothing to show here.</p> : pages.map((p) => <PageRow key={p.path} p={p} base={d.base} />)}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
